import {
  createContext,
  forwardRef,
  useContext,
  useId,
  type HTMLAttributes,
  type ReactNode,
} from 'react';

import { cn } from '../lib/cn.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * FIELD — THE LABEL/DESCRIPTION/ERROR WRAPPER EVERY FORM CONTROL HERE USES.
 * ═══════════════════════════════════════════════════════════════════════════
 * Follows the five rules in `Button.tsx`. What is specific to a Field is the wiring, and the
 * wiring is the entire point of the component: three ids, generated once, connected in the
 * three different ways the accessibility tree requires.
 *
 * ── 1. THE NAME COMES FROM A REAL `<label for>` ────────────────────────────
 * Not `aria-label`, not a `<span>` that merely sits above the input. A real label is the only
 * one of the three that also gives a pointer user a second, larger hit target — clicking the
 * word "Extension" focuses the box — and WCAG 2.5.3 (Label in Name) is satisfied by
 * construction because the visible text *is* the accessible name rather than a copy of it that
 * somebody will later edit in one place only.
 *
 * ── 2. DESCRIPTION **AND** ERROR, BOTH AT ONCE ─────────────────────────────
 * The bug this component exists to prevent: `aria-describedby={error ? errorId : descId}`.
 * It reads as reasonable and it is wrong — the moment a field goes invalid, the format hint
 * that says *how to fix it* is removed from the accessibility tree, so the screen-reader user
 * is told "Must be unique" and simultaneously loses "Digits only, 3 to 6 of them". Sighted
 * users see both. `aria-describedby` takes a LIST; both ids go in it, in DOM order, so what is
 * heard matches what is read.
 *
 * `aria-errormessage` is deliberately NOT used for the error. It is the semantically precise
 * attribute and its support is still uneven enough that a message can land nowhere at all,
 * whereas `aria-describedby` is universally implemented. Precision is not worth an error
 * message that some users never hear.
 *
 * ── 3. THE ERROR SLOT IS A LIVE REGION THAT IS ALWAYS MOUNTED ──────────────
 * `aria-describedby` is announced when the control takes focus. That covers the user who tabs
 * into a field that is already invalid, and covers nobody at all when a server rejects a save
 * and the message appears while focus is on the Save button. So the slot is also a polite live
 * region — and it is rendered EMPTY rather than conditionally, because a live region that is
 * inserted into the DOM already containing its text is frequently not announced: assistive
 * technology watches registered regions for mutations, and a node that arrives complete had no
 * mutation to observe. Mount the container, change the contents.
 *
 * `polite`, not `assertive`/`role="alert"`: a validation message is not worth interrupting a
 * sentence mid-word, and on a form that reports several fields at once `assertive` produces a
 * pile-up in which each announcement cancels the last.
 *
 * ── 4. WHY CONTEXT AND NOT A RENDER PROP (both were on the table) ──────────
 * A render prop's failure mode is silent. `{(props) => <Input />}` — spread forgotten —
 * renders a pixel-perfect field with no accessible name, no description and no invalid state,
 * and nothing anywhere reports it; it fails only for the people who cannot see the layout that
 * makes it *look* connected. Context makes the correct wiring the default: `<Field><Input/>
 * </Field>` is complete, and there is no step to omit.
 *
 * The render-prop form is kept as an explicit escape hatch, because context only reaches
 * components that opt into reading it — a third-party date picker or a bare `<textarea>`
 * cannot. There the spread is visible in the diff, which is where a wiring mistake should be.
 */

/**
 * What the control has to receive for the field to be wired. Every member is either present
 * with a meaningful value or absent — never present-and-undefined, which under
 * `exactOptionalPropertyTypes` is a different thing, and which on a DOM element would render
 * `aria-invalid="false"` where the attribute should not exist at all.
 */
export interface FieldControlAria {
  id: string;
  /**
   * The id of the Field's own `<label>` element.
   *
   * Present so a control can COMPOSE its name rather than only inherit it. `Select` needs
   * this: a `role="combobox"` button has no separate value property — its value is its content
   * — so naming it by the label alone drops the current selection from what is announced. It
   * references `labelId` AND its own value node to get "Phone type, Desk phone" rather than
   * "Phone type". A plain `<input>` never needs it, because the native `<label for>`
   * association already does the whole job there.
   */
  labelId: string;
  'aria-describedby'?: string;
  'aria-invalid'?: true;
  required?: boolean;
}

const FieldControlContext = createContext<FieldControlAria | null>(null);

/**
 * `null` outside a Field, and that is the useful distinction rather than an inconvenience: a
 * control uses it to tell "my Field computed no description" from "I am not in a Field", and
 * only the second case means its own `id` prop should win.
 */
export function useFieldControl(): FieldControlAria | null {
  return useContext(FieldControlContext);
}

export interface FieldProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children' | 'id'> {
  /** The visible label. It is also the accessible name — there is no second copy to drift. */
  label: ReactNode;
  /** Persistent help: format, units, consequences. Announced alongside any error, not instead. */
  description?: ReactNode;
  /** Truthy marks the control invalid. Falsy is not an error, so `error={false}` is fine. */
  error?: ReactNode;
  required?: boolean;
  /**
   * Visually hide the label while keeping it in the accessibility tree — for a field whose
   * purpose is already obvious from a column header or a search icon. It is `sr-only`, never
   * `display: none`, and it is never a substitute for having a label at all.
   */
  labelHidden?: boolean;
  /**
   * Override the generated control id. Only for a field a deep link or an external
   * `aria-controls` has to be able to name; `useId` is otherwise correct and collision-free.
   */
  controlId?: string;
  children: ReactNode | ((control: FieldControlAria) => ReactNode);
}

export const Field = forwardRef<HTMLDivElement, FieldProps>(function Field(
  {
    label,
    description,
    error,
    required = false,
    labelHidden = false,
    controlId,
    children,
    className,
    ...rest
  },
  ref,
) {
  /**
   * One `useId` for the whole field, three ids derived from it. `useId` and not a module-level
   * counter because the counter desynchronises between the server render and the client
   * hydration, and the symptom is a `for` that points at nothing on exactly the first paint —
   * the render a screen reader may already be reading.
   */
  const uid = useId();
  const id = controlId ?? `${uid}control`;
  const labelId = `${uid}label`;
  const descriptionId = `${uid}description`;
  const errorId = `${uid}error`;

  const hasDescription = Boolean(description);
  const hasError = Boolean(error);

  // DOM order, so the order it is announced in is the order it is laid out in.
  const describedBy = [hasDescription ? descriptionId : null, hasError ? errorId : null]
    .filter((value): value is string => value !== null)
    .join(' ');

  const control: FieldControlAria = {
    id,
    labelId,
    ...(describedBy ? { 'aria-describedby': describedBy } : {}),
    ...(hasError ? { 'aria-invalid': true as const } : {}),
    ...(required ? { required: true } : {}),
  };

  return (
    <FieldControlContext.Provider value={control}>
      <div ref={ref} className={cn('w-full', className)} {...rest}>
        <label
          id={labelId}
          htmlFor={id}
          className={cn('block text-sm font-medium text-fg', labelHidden && 'sr-only')}
        >
          {label}
          {/**
           * The asterisk is `aria-hidden` on purpose. `required` is already on the control, so
           * the state reaches assistive technology from the attribute that means it; repeating
           * it here produces "Extension star, required, edit text". WCAG 1.4.1 is satisfied
           * because the asterisk is not the *only* carrier of the information — the attribute
           * is the primary one, and the mark is the sighted-user affordance for it.
           */}
          {required ? (
            <span aria-hidden="true" className="ml-0.5 text-danger">
              *
            </span>
          ) : null}
        </label>

        {hasDescription ? (
          <p id={descriptionId} className="mt-1 text-sm text-fg-muted">
            {description}
          </p>
        ) : null}

        <div className="mt-1.5">
          {typeof children === 'function' ? children(control) : children}
        </div>

        {/**
         * Always rendered (see header §3). The margin sits on the inner paragraph rather than
         * on this container so that an empty region occupies no vertical space and the field
         * does not jump by a few pixels the first time it goes invalid — a layout shift under
         * the user's pointer at the exact moment they are trying to fix something.
         */}
        <div id={errorId} aria-live="polite" aria-atomic="true">
          {hasError ? (
            <p className="mt-1.5 text-sm font-medium text-danger">{error}</p>
          ) : null}
        </div>
      </div>
    </FieldControlContext.Provider>
  );
});
